/**
 * StaffPicker.jsx
 *
 * Cross-browser combobox for the Medical Team Member field: a free-text input
 * with a suggestion dropdown drawn from the directory (options). Unlike a native
 * <datalist> (which Safari doesn't render), this works everywhere — and you can
 * still type any name (the manual fallback) when someone isn't listed.
 *
 * options: Array<{ name, jobTitle?, email? }>
 */
import React, { useState, useRef, useEffect } from 'react';

export default function StaffPicker({ value, onChange, options = [], placeholder, id }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const q = (value || '').toLowerCase().trim();
  const matches = options
    .filter(s => !q || s.name.toLowerCase().includes(q) || (s.jobTitle || '').toLowerCase().includes(q))
    .slice(0, 30);

  // Group suggestions by department (sorted), so people are organised by team.
  const groups = {};
  for (const s of matches) (groups[s.department || 'Other'] = groups[s.department || 'Other'] || []).push(s);
  const deptKeys = Object.keys(groups).sort();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        className="input"
        autoComplete="off"
        placeholder={placeholder}
        value={value || ''}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div role="listbox" className="plain-pop dd-shell" style={{ zIndex: 50 }}>
          {deptKeys.map(dept => (
            <div key={dept}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', padding: '6px var(--dd-item-px) 3px' }}>{dept}</div>
              {groups[dept].map(s => (
                <button
                  key={s.email || s.name}
                  type="button"
                  role="option"
                  onClick={() => { onChange(s.name); setOpen(false); }}
                  className="dd-item dd-two"
                >
                  <b>{s.name}</b>
                  {s.jobTitle && <span>{s.jobTitle}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
